import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';

const IS_DEMO = import.meta.env.VITE_DEMO_MODE === 'true';
const SESSION_KEY = 'raporgor.demo.session';

interface AuthContextValue {
  isAuthenticated: boolean;
  isDemo: boolean;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    // Non-demo builds always start unauthenticated (Sprint 2 will wire real JWT).
    if (!IS_DEMO) return false;
    return sessionStorage.getItem(SESSION_KEY) === 'active';
  });

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === SESSION_KEY) setIsAuthenticated(e.newValue === 'active');
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const login = useCallback(() => {
    if (IS_DEMO) {
      sessionStorage.setItem(SESSION_KEY, 'active');
      setIsAuthenticated(true);
    }
    // Non-demo: real JWT flow wired in Sprint 2.
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY);
    setIsAuthenticated(false);
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, isDemo: IS_DEMO, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
